import { apiRoutes } from '@/shared/api/routes';
import { AxiosInstance } from 'axios';
import { ViewSettings, PageSetting, PageSize, ColumnValueType, StopFilterValuesType, RefreshPeriodValues } from '@/shared/components/viewSettings/types';

const mapRefreshPeriodToSeconds = (refreshPeriod: RefreshPeriodValues): number | undefined => {
  if (refreshPeriod === '') return undefined;
  const periodInSeconds: { [key in RefreshPeriodValues]?: number } = {
    1: 60,
    2: 120,
    5: 300,
    10: 600,
  };
  return periodInSeconds[refreshPeriod];
};

const mapRefreshPeriod = (seconds: number | undefined): RefreshPeriodValues => {
    if (seconds === undefined) return '';
    if (seconds <= 60) return 1;
    if (seconds <= 120) return 2;
    if (seconds <= 300) return 5;
    return 10;
}

export const getPreferences = async (apiClient: AxiosInstance): Promise<ViewSettings> => {
  const response = await apiClient.get(apiRoutes.proxy('support/internal/session/me/preferences'));
  
  const defaultPageSettings: PageSetting[] = [
    { id: 'default', page: 'default', pageSize: 50, search: false },
    { id: 'customer', page: 'customer', pageSize: 50, search: true, columns: 'basic' },
    { id: 'customer_decision', page: 'customer_decision', columns: 'basic' },
    { id: 'dashboard', page: 'dashboard', refreshPeriod: '' },
    { id: 'alert', page: 'alert', pageSize: 50, search: false, refreshPeriod: '' },
    { id: 'run_status', page: 'run_status', pageSize: 50, search: false, columns: 'basic' },
    { id: 'route', page: 'route', pageSize: 10, search: false, columns: 'basic', stopFilter: 'after', dragAndDrop: 'true' },
    { id: 'trip', page: 'trip', pageSize: 50, search: false, columns: 'basic' },
    { id: 'subscription', page: 'subscription', pageSize: 50, search: false, columns: 'basic' },
    { id: 'archive_route', page: 'archive_route', pageSize: 10, search: false, columns: 'basic' },
    { id: 'archive_trip', page: 'archive_trip', pageSize: 50, search: false, columns: 'basic' },
  ];

  const items: any[] = Array.isArray(response.data?.data) ? response.data.data : [];
  const userPreference = items.find((pref: any) => pref.key === 'profile');
  
  if (!userPreference) {
      return {
        startingDayOfWeek: 'Sunday',
        floatTableHeaders: true,
        pageSettings: defaultPageSettings,
      };
  }

  const parsedValue = typeof userPreference.value === 'string' ? JSON.parse(userPreference.value) : userPreference.value;
  const { view } = parsedValue;
  const { start_dow, model_preferences, float_header } = view;

  let pageSettings: PageSetting[];

  if (model_preferences) {
    pageSettings = defaultPageSettings.map((defaultSetting) => {
      const pref = model_preferences.find((p: any) => p.id === defaultSetting.id);
      const newSetting = { ...defaultSetting };

      if (pref) {
        if (defaultSetting.pageSize !== undefined) {
          newSetting.pageSize = (pref.page_size || defaultSetting.pageSize) as PageSize;
        }
        if (defaultSetting.search !== undefined) {
          newSetting.search = pref.auto_search !== undefined ? pref.auto_search : defaultSetting.search;
        }
        if (defaultSetting.columns !== undefined) {
          newSetting.columns = (pref.column_set || defaultSetting.columns) as ColumnValueType;
        }
        newSetting.stopFilter = (pref.stop_filter || defaultSetting.stopFilter) as StopFilterValuesType;
        if (pref.drag_n_drop !== undefined) {
          newSetting.dragAndDrop = String(pref.drag_n_drop);
        }
        newSetting.refreshPeriod = (mapRefreshPeriod(pref.refresh_period) || defaultSetting.refreshPeriod) as RefreshPeriodValues;
      }
      return newSetting;
    });
  } else {
    pageSettings = defaultPageSettings;
  }

  return {
    startingDayOfWeek: start_dow ? start_dow.charAt(0).toUpperCase() + start_dow.slice(1) : 'Sunday',
    floatTableHeaders: float_header !== undefined ? float_header : true,
    pageSettings,
  };
};

export const updatePreferences = async (apiClient: AxiosInstance, settings: ViewSettings) => {
  const model_preferences = settings.pageSettings.map((page) => {
    const apiPage: any = { id: page.id };

    if (page.pageSize) {
      apiPage.page_size = Number(page.pageSize);
    }
    if (page.search !== undefined) {
      apiPage.auto_search = page.search;
    }
    if (page.columns) {
      apiPage.column_set = page.columns;
    }
    if (page.stopFilter) {
      apiPage.stop_filter = page.stopFilter;
    }
    if (page.dragAndDrop !== undefined && page.dragAndDrop !== '') {
      apiPage.drag_n_drop = page.dragAndDrop === 'true';
    }
    if (page.refreshPeriod) {
      apiPage.refresh_period = mapRefreshPeriodToSeconds(page.refreshPeriod);
    }
    return apiPage;
  });

  const value = {
    view: {
      start_dow: settings.startingDayOfWeek.toLowerCase(),
      float_header: settings.floatTableHeaders,
      model_preferences,
    },
  };

  const payload = {
    key: 'profile',
    value: JSON.stringify(value),
    id: 1,
  };

  const response = await apiClient.put(apiRoutes.proxy('support/internal/session/me/preferences'), payload);
  return response.data;
};